import React, { useEffect, useMemo, useRef, useState } from "react";
import "./FilterModel.css";
import { FaStar } from "react-icons/fa";
import Slider from "@mui/material/Slider";
import axios from "axios";
import { API_BASE_URL } from "../utils";
import { useQuery } from '@tanstack/react-query';

export default function FilterModal({
    isOpen,
    onClose,
    setFiltersAndSort,
    fetchDataWithFilters,
    currentFilters = {},
}) {
    // ---------------- Local (staged) state ONLY ----------------
    const [min, setMin] = useState(0);
    const [max, setMax] = useState(1000);
    const [min2, setMin2] = useState(90);
    const [max2, setMax2] = useState(500);

    const [value, setValue] = useState([0, 1000]); // distance [km]
    const [value2, setValue2] = useState([90, 500]); // price [₹]

    const [selectedCities, setSelectedCities] = useState([]);
    const [selectedAreas, setSelectedAreas] = useState([]);
    const [selectedVenues, setSelectedVenues] = useState([]);   // venue type IDs
    const [selectedPackages, setSelectedPackages] = useState([]); // package IDs (FIXED)
    const [selectedReview, setSelectedReview] = useState(null); // 1..5 or null
    const [sitting, setSitting] = useState([]); // ["Indoor","Outdoor"]



    const { data: venueData = [], isLoading: isLoadingVenue } = useQuery({
        queryKey: ['venueTypes'],
        queryFn: async () => {
            const response = await axios.get(`${API_BASE_URL}/master/APIs/ClubPackage/getVenuType`);
            return response.data?.data ?? [];
        },
        staleTime: 1000 * 60 * 60 * 5,      // 5 hours cache
        cacheTime: 1000 * 60 * 60 * 24,     // 24 hours
        refetchOnWindowFocus: false,
        refetchOnReconnect: false
    });




    const { data: packageData = [], isLoading: isLoadingPackage } = useQuery({
        queryKey: ['packageTitles'],
        queryFn: async () => {
            const response = await axios.get(`${API_BASE_URL}/master/APIs/Common/getAllPackageTitle`);
            return response.data?.data ?? [];
        },
        staleTime: 1000 * 60 * 60 * 5,
        cacheTime: 1000 * 60 * 60 * 24,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false
    });




    const { data: city = [], isLoading: isLoadingCity } = useQuery({
        queryKey: ['cityLeads'],
        queryFn: async () => {
            const response = await axios.get(`${API_BASE_URL}/master/Api/getData/getAreaLead`);
            return response.data?.data ?? [];
        },
        staleTime: 1000 * 60 * 60 * 5,
        cacheTime: 1000 * 60 * 60 * 24,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false
    });


    // ---------------- Sync modal with parent filters on open ----------------
    useEffect(() => {
        if (!isOpen) return;

        // Distance/price
        const dist = currentFilters.distance ?? [min, max];
        const price = currentFilters.price ?? [min2, max2];

        setValue([Number(dist[0]) || min, Number(dist[1]) || max]);
        setValue2([Number(price[0]) || min2, Number(price[1]) || max2]);

        setSelectedCities(currentFilters.city ?? []);
        setSelectedAreas(currentFilters.area ?? []);
        setSelectedVenues(currentFilters.venue ?? []);
        setSelectedPackages(currentFilters.package ?? []);

        // Optional extras if parent stored them
        setSelectedReview(
            typeof currentFilters.review === "number" ? currentFilters.review : null
        );
        setSitting(Array.isArray(currentFilters.sitting) ? currentFilters.sitting : []);
    }, [isOpen]); // eslint-disable-line

    // ---------------- Fetch areas when local selectedCities changes ----------------

    // Fetch areas for multiple cities
    const fetchAreasByCities = async ({ queryKey }) => {
        const [, selectedCities] = queryKey;
        const apiUrl = `${API_BASE_URL}/master/Api/getDataPost/AreaLead`;

        const areaResponses = await Promise.all(
            selectedCities.map((cityId) => {
                const formData = new FormData();
                formData.append("city_id", cityId);
                return axios.post(apiUrl, formData);
            })
        );

        // Flatten and remove duplicates
        const allAreas = areaResponses.flatMap((res) => res.data?.data ?? []);
        const uniqueAreas = Array.from(new Map(allAreas.map((a) => [a.id, a])).values());
        return uniqueAreas;
    };

    // React Query hook
    const {
        data: area = [],
        isLoading: isLoadingArea,
        isError: isErrorArea,
        error: errorArea,
    } = useQuery({
        queryKey: ['areas', selectedCities],
        queryFn: fetchAreasByCities,
        enabled: selectedCities.length > 0, // fetch only if there are selected cities
        staleTime: 1000 * 60 * 5,  // 5 minutes
        cacheTime: 1000 * 60 * 30, // 30 minutes
        refetchOnWindowFocus: false,
        refetchOnReconnect: false
    });

    // ---------------- Handlers (local only) ----------------
    const url = `${API_BASE_URL}/master/assets/uploads/`;

    const handleChangeDistance = (_, newValue) => setValue(newValue);
    const handleChangePrice = (_, newValue) => setValue2(newValue);

    const handleSelectVenue = (venueId) => {
        setSelectedVenues((prev) =>
            prev.includes(venueId) ? prev.filter((id) => id !== venueId) : [...prev, venueId]
        );
    };

    // FIXED: push package **IDs** in state
    const handleSelectPackage = (pkgId) => {
        setSelectedPackages((prev) =>
            prev.includes(pkgId) ? prev.filter((id) => id !== pkgId) : [...prev, pkgId]
        );
    };

    const handleCityChange = (cityId) => {
        setSelectedCities((prev) =>
            prev.includes(cityId) ? prev.filter((id) => id !== cityId) : [...prev, cityId]
        );
    };

    const handleAreaChange = (areaId) => {
        setSelectedAreas((prev) =>
            prev.includes(areaId) ? prev.filter((id) => id !== areaId) : [...prev, areaId]
        );
    };

    const handleReviewClick = (val) =>
        setSelectedReview((prev) => (prev === val ? null : val));

    const toggleSitting = (type) =>
        setSitting((prev) =>
            prev.includes(type) ? prev.filter((x) => x !== type) : [...prev, type]
        );

    // Clear locally (do NOT notify parent)
    const handleClearLocal = () => {
        setSelectedCities([]);
        setSelectedAreas([]);
        setSelectedVenues([]);
        setSelectedPackages([]);
        setSelectedReview(null);
        setSitting([]);
        setValue([min, max]);
        setValue2([min2, max2]);
    };

    // Commit to parent ONLY here
    const handleShowResults = async () => {
        setFiltersAndSort({
            city: selectedCities,
            area: selectedAreas,
            venue: selectedVenues,
            package: selectedPackages, // IDs
            distance: value,
            price: value2,
            review: selectedReview,
            sitting,                // optional
        });

        onClose();
        await fetchDataWithFilters();
    };


    const marks = useMemo(
        () => [
            { value: min, label: `${min} km` },
            { value: max, label: `${max} km` },
        ],
        [min, max]
    );

    const marks2 = useMemo(
        () => [
            { value: min2, label: `₹${min2}` },
            { value: max2, label: `₹${max2}` },
        ],
        [min2, max2]
    );

    if (!isOpen) return null;
    return (
        <div className="lpfm-modal-backdrop" onClick={onClose}>
            <div className="lpfm-filter-modal" onClick={(e) => e.stopPropagation()}>
                <div className="lpfm-filter-header">
                    <h3>Filter</h3>
                    <button className="lpfm-close-btn" onClick={onClose}>×</button>
                </div>

                <div className="lpfm-modal-content">
                    {/* Type of Venue */}
                    <div className="lpfm-filter-section">
                        <h4>Type of Venue</h4>
                        <div className="lpfm-filter-options-venue lpfm-venue-icons">
                            {venueData.map((item) => (
                                <div
                                    key={item.id}
                                    className={
                                        selectedVenues.includes(item.id)
                                            ? "f-m-type-of-venue-active"
                                            : "f-m-type-of-venue"
                                    }
                                    onClick={() => handleSelectVenue(item.id)}
                                >
                                    <img src={url + item.img} alt={item.name} />
                                    <div className="f-m-type-of-venue-overlay">
                                        <p>{item.name}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Type of Packages */}
                    <div className="lpfm-filter-section">
                        <h4>Type of Packages</h4>
                        <div className="lpfm-filter-options-package lpfm-pills">
                            {packageData.map((item) => (
                                <div
                                    key={item.id}
                                    className={
                                        selectedPackages.includes(item.id)
                                            ? "f-m-type-of-package-active"
                                            : "f-m-type-of-package"
                                    }
                                    onClick={() => handleSelectPackage(item.id)} // IDs (FIXED)
                                >
                                    {item.name}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Near By (Distance) */}
                    <div className="lpfm-filter-section">
                        <h4>Near By</h4>
                        <div className="lpfm-range-bar">
                            <Slider
                                getAriaLabel={() => "distance range"}
                                value={value}
                                min={min}
                                max={max}
                                onChange={handleChangeDistance}
                                valueLabelDisplay="off"
                                marks={marks}
                            />
                            <p className="lpfm-price-range">Distance Range: {value[0]}km - {value[1]}km</p>
                        </div>
                    </div>

                    {/* Price */}
                    <div className="lpfm-filter-section">
                        <h4>Price</h4>
                        <div className="lpfm-range-bar">
                            <Slider
                                getAriaLabel={() => "Price range"}
                                value={value2}
                                min={min2}
                                max={max2}
                                onChange={handleChangePrice}
                                valueLabelDisplay="off"
                                marks={marks2}
                            />
                            <p className="lpfm-price-range">Price Range: ₹{value2[0]} - ₹{value2[1]}</p>
                        </div>
                    </div>

                    {/* Review */}
                    <div className="lpfm-filter-section">
                        <h4>Review</h4>
                        <div className="lpfm-filter-options lpfm-stars">
                            {[5, 4, 3, 2, 1].map((val) => (
                                <button
                                    key={val}
                                    className={selectedReview === val ? "active" : ""}
                                    onClick={() => handleReviewClick(val)}
                                >
                                    {val} <FaStar />
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Sitting */}
                    <div className="lpfm-filter-section">
                        <h4>Sitting</h4>
                        <div className="lpfm-filter-options lpfm-pills">
                            <button
                                className={sitting.includes("Indoor") ? "active" : ""}
                                onClick={() => toggleSitting("Indoor")}
                            >
                                Indoor
                            </button>
                            <button
                                className={sitting.includes("Outdoor") ? "active" : ""}
                                onClick={() => toggleSitting("Outdoor")}
                            >
                                Outdoor
                            </button>
                        </div>
                    </div>

                    {/* City & Area */}
                    <div className="lpfm-filter-section">
                        <h4>City & Area</h4>
                        <div className="lpfm-cities-areas-city">
                            <div className="lpfm-city-area">
                                <p>City</p>
                                {city.map((item) => (
                                    <label key={item.id}>
                                        <input
                                            type="checkbox"
                                            onChange={() => handleCityChange(item.id)}
                                            checked={selectedCities.includes(item.id)}
                                        />
                                        {item.name}
                                    </label>
                                ))}
                            </div>

                            <div className="lpfm-city-area">
                                <p>Area</p>
                                {area.length === 0 && (
                                    <span className="lpfm-select-city-no-data">Select city first</span>
                                )}
                                {area.map((item) => (
                                    <label key={item.id}>
                                        <input
                                            type="checkbox"
                                            onChange={() => handleAreaChange(item.id)}
                                            checked={selectedAreas.includes(item.id)}
                                        />
                                        {item.name}
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="lpfm-filter-footer">
                    <button className="lpfm-clear-btn" onClick={handleClearLocal}>
                        Clear Filter
                    </button>
                    <button className="lpfm-result-btn" onClick={handleShowResults}>
                        Show results
                    </button>
                </div>
            </div>
        </div>
    );
}
